// src/hooks/useAudioEngine.js
import { useCallback, useEffect, useRef } from "react";

// Each track node is:
// {
//   audioEl,      // HTMLAudioElement
//   source,       // MediaElementAudioSourceNode
//   gainNode,     // GainNode - controls volume (0 to 10+)
//   audioCtx,     // AudioContext
// }

const useAudioEngine = ({
  audioTracks = [],
  isPlaying,
  currentTime,
  duration,
}) => {
  const nodesRef = useRef([]); // array of { audioEl, gainNode, audioCtx }
  const fadeTimersRef = useRef([]);
  const lastSeekTimeRef = useRef(null);

  const DRIFT_CATCH_UP_THRESHOLD = 0.08;
  const DRIFT_REWIND_THRESHOLD = 0.35;

  const safelyCloseAudioContext = useCallback((audioCtx) => {
    if (!audioCtx || audioCtx.state === "closed") return;
    audioCtx.close().catch(() => {});
  }, []);

  const teardownNodes = useCallback(() => {
    nodesRef.current.forEach((node) => {
      if (!node) return;
      try {
        node.audioEl.pause();
        node.audioEl.src = "";
      } catch {}
      try {
        node.source.disconnect();
      } catch {}
      try {
        node.gainNode.disconnect();
      } catch {}
      safelyCloseAudioContext(node.audioCtx);
    });
    fadeTimersRef.current.forEach(clearInterval);
  }, [safelyCloseAudioContext]);

  // Build nodes when tracks change
  useEffect(() => {
    teardownNodes();

    if (!audioTracks.length) {
      nodesRef.current = [];
      fadeTimersRef.current = [];
      return;
    }

    nodesRef.current = audioTracks.map((track) => {
      if (!track.url) return null;

      try {
        const audioCtx = new (
          window.AudioContext || window.webkitAudioContext
        )();
        const audioEl = new Audio(track.url);
        audioEl.crossOrigin = "anonymous";
        audioEl.loop = track.loop ?? false;
        audioEl.preload = "auto";

        const source = audioCtx.createMediaElementSource(audioEl);
        const gainNode = audioCtx.createGain();
        gainNode.gain.value = 0; // sync() sets correct gain

        source.connect(gainNode);
        gainNode.connect(audioCtx.destination);

        return { audioEl, source, gainNode, audioCtx };
      } catch {
        return null;
      }
    });

    fadeTimersRef.current = new Array(audioTracks.length).fill(null);

    return teardownNodes;
  }, [audioTracks, teardownNodes]);

  const setGain = useCallback((node, value) => {
    if (!node?.gainNode) return;
    // GainNode.gain has no upper limit - supports 0 to 10+
    node.gainNode.gain.value = Math.max(0, value);
  }, []);

  const sync = useCallback(
    (time, playing) => {
      audioTracks.forEach((track, i) => {
        const node = nodesRef.current[i];
        if (!node) return;

        const { audioEl, audioCtx } = node;
        const targetGain = track.volume ?? 1; // volume is now gain (0-10+)
        const trackStart = track.start ?? 0;
        const fadeIn = track.fadeIn ?? 0;
        const fadeOut = track.fadeOut ?? 0;
        const trackDuration = audioEl.duration || Infinity;
        const trackEnd = track.loop ? Infinity : trackStart + trackDuration;
        const isActive = time >= trackStart && time < trackEnd;

        if (!playing || !isActive) {
          if (!audioEl.paused) audioEl.pause();
          return;
        }

        // Resume AudioContext if suspended (browser autoplay policy)
        if (audioCtx.state === "suspended") {
          audioCtx.resume().catch(() => {});
        }

        // Correct drift > 150ms
        const expectedOffset = time - trackStart;
        const loopedOffset =
          track.loop && trackDuration > 0
            ? expectedOffset % trackDuration
            : expectedOffset;

        const drift = loopedOffset - audioEl.currentTime;
        const isRecentSeek =
          lastSeekTimeRef.current != null &&
          Math.abs(lastSeekTimeRef.current - time) < 0.001;
        const needsResync =
          isRecentSeek ||
          drift > DRIFT_CATCH_UP_THRESHOLD ||
          drift < -DRIFT_REWIND_THRESHOLD;

        // Avoid rewinding audio during normal playback. When the render loop lags
        // behind real media playback, snapping backward causes repeated words.
        if (needsResync) {
          audioEl.currentTime = loopedOffset;
          setGain(node, targetGain);
          return;
        }

        if (audioEl.seeking) {
          setGain(node, targetGain);
          return;
        }

        // Compute gain with fade in/out
        let gain = targetGain;
        const timeInTrack = time - trackStart;
        if (fadeIn > 0 && timeInTrack < fadeIn) {
          gain = targetGain * (timeInTrack / fadeIn);
        } else if (fadeOut > 0 && !track.loop && time > trackEnd - fadeOut) {
          gain = targetGain * ((trackEnd - time) / fadeOut);
        }
        setGain(node, gain);

        if (audioEl.paused) {
          audioEl.play().catch(() => {});
        }
      });
    },
    [audioTracks, setGain],
  );

  // Sync on play state / time change
  useEffect(() => {
    if (!audioTracks.length) return;
    sync(currentTime, isPlaying);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, currentTime]);

  const seekAudio = useCallback(
    (time) => {
      lastSeekTimeRef.current = time;
      audioTracks.forEach((track, i) => {
        const node = nodesRef.current[i];
        if (!node) return;

        const { audioEl } = node;
        const trackStart = track.start ?? 0;
        const trackDuration = audioEl.duration || Infinity;
        const trackEnd = track.loop ? Infinity : trackStart + trackDuration;

        audioEl.pause();

        if (time >= trackStart && time < trackEnd) {
          const offset = time - trackStart;
          const loopedOffset =
            track.loop && trackDuration > 0 ? offset % trackDuration : offset;
          audioEl.currentTime = loopedOffset;
        }
      });
    },
    [audioTracks],
  );

  const stopAll = useCallback(() => {
    nodesRef.current.forEach((node) => {
      if (!node) return;
      node.audioEl.pause();
      node.audioEl.currentTime = 0;
      setGain(node, 0);
    });
    fadeTimersRef.current.forEach(clearInterval);
  }, [setGain]);

  return { sync, seekAudio, stopAll };
};

export default useAudioEngine;
